import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { isValidEmail, validatePasswordPolicy, maskEmail } from '../services/auth-validation';

/** The federated identity providers the Application offers (Req 5.1). */
type FederatedProvider = 'Google' | 'Facebook' | 'Apple';

/**
 * Result of a registration request.
 *
 * Non-enumerating: on an existing-email path Cognito (with
 * `preventUserExistenceErrors`) resolves to the same "verification required"
 * outcome as a fresh sign-up, so the UI cannot distinguish the two (Req 1.7).
 */
interface RegisterResult {
  nextStep: 'CONFIRM_SIGN_UP';
  /** Partially-masked email to display on the verify screen (Req 3.3). */
  destinationMasked?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  error: string | null;
  /**
   * Epoch-ms timestamp until which the resend control must remain disabled, or
   * `null` when no cooldown is active. Lets the UI disable the resend control
   * for the 30-second cooldown window (Req 3.5).
   */
  resendCooldownUntil: number | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  register: (email: string, password: string) => Promise<RegisterResult>; // Req 1
  confirmRegistration: (email: string, code: string) => Promise<void>; // Req 2
  resendCode: (email: string) => Promise<void>; // Req 3
  signInWithProvider: (provider: FederatedProvider) => Promise<void>; // Req 5.3
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours
const RESEND_COOLDOWN_MS = 30 * 1000; // 30-second resend cooldown (Req 3.5)

// User-facing messages, kept centralized so they stay consistent and
// non-enumerating (Error Handling table).
const PASSWORD_POLICY_MESSAGE =
  'Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a digit, and a symbol.';
const INVALID_EMAIL_MESSAGE = 'Enter a valid email address.';
const REGISTRATION_FAILED_MESSAGE =
  "Registration couldn't be completed. Please try again.";
const CODE_INCORRECT_MESSAGE = 'That code is incorrect.';
const CODE_EXPIRED_MESSAGE = 'That code has expired.';
const RESEND_RATE_LIMIT_MESSAGE = 'Too many requests, please wait.';
const PROVIDER_SIGNIN_FAILED_MESSAGE =
  'Sign-in with that provider could not be completed.';

/** Narrow an unknown thrown value to its Cognito/Amplify error name, if any. */
function errorName(err: unknown): string {
  return err instanceof Error ? err.name : '';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    token: null,
    error: null,
    resendCooldownUntil: null,
  });

  const lastActivityRef = useRef<number>(Date.now());
  const sessionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Mirrors `resendCooldownUntil` for synchronous cooldown checks inside
  // `resendCode` without depending on the latest render's state snapshot.
  const resendCooldownUntilRef = useRef<number | null>(null);

  // Track user activity for session expiry detection
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, []);

  // Session inactivity check
  useEffect(() => {
    if (!state.isAuthenticated) {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
        sessionTimerRef.current = null;
      }
      return;
    }

    sessionTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= SESSION_TIMEOUT_MS) {
        handleSessionExpiry();
      }
    }, 60_000); // Check every minute

    return () => {
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, [state.isAuthenticated]);

  const handleSessionExpiry = useCallback(async () => {
    try {
      await signOut();
    } catch {
      // Ignore sign-out errors during expiry
    }
    setState({
      isAuthenticated: false,
      isLoading: false,
      token: null,
      error: 'Session expired due to inactivity. Please log in again.',
      resendCooldownUntil: null,
    });
  }, []);

  // Check existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  // OAuth-redirect handling: subscribe to the Amplify Hub 'auth' channel so the
  // post-redirect federated session (or failure) is picked up (Req 5.8-5.10,
  // 10.2, 10.3). Amplify only fires the success event once it has validated the
  // returned OAuth state + PKCE, so a state/PKCE mismatch surfaces on the
  // failure path and never establishes a session.
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          // Reuse the existing session-pickup logic.
          checkSession();
          break;
        case 'signInWithRedirect_failure':
          // Covers denied consent (Req 5.9), provider outage/error (Req 5.10),
          // and OAuth state/PKCE mismatch (Req 10.2, 10.3) — no session.
          setState({
            isAuthenticated: false,
            isLoading: false,
            token: null,
            error: PROVIDER_SIGNIN_FAILED_MESSAGE,
            resendCooldownUntil: null,
          });
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, []);

  const checkSession = async () => {
    try {
      // First check if there's a current user (uses localStorage)
      const user = await getCurrentUser();
      console.log('[Auth] checkSession: current user found', user.username);

      // User exists, fetch tokens
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString() ?? null;
      console.log('[Auth] checkSession: session tokens', { hasIdToken: !!idToken });

      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
        isLoading: false,
        token: idToken,
        error: null,
      }));
    } catch (err) {
      // No current user or session expired
      console.log('[Auth] checkSession: no authenticated user');
      setState((prev) => ({
        ...prev,
        isAuthenticated: false,
        isLoading: false,
        token: null,
        error: null,
      }));
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      // Sign out any existing session first to avoid "already signed in" error
      try {
        await signOut();
      } catch {
        // Ignore — there may not be an existing session
      }

      console.log('[Auth] login: calling signIn...');
      const signInResult = await signIn({ username: email, password });
      console.log('[Auth] login: signInResult', { isSignedIn: signInResult.isSignedIn, nextStep: signInResult.nextStep });

      // Handle sign-in challenges (e.g. NEW_PASSWORD_REQUIRED for admin-created users)
      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setState((prev) => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false,
          token: null,
          error: 'A new password is required. Please set a new password in the Cognito console or contact your administrator.',
        }));
        return;
      }

      if (!signInResult.isSignedIn) {
        setState((prev) => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false,
          token: null,
          error: `Sign-in incomplete: ${signInResult.nextStep?.signInStep || 'additional verification required'}.`,
        }));
        return;
      }

      // Sign-in succeeded — fetch the session tokens
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString() ?? null;
      console.log('[Auth] login: session after signIn', { hasIdToken: !!idToken });

      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
        isLoading: false,
        token: idToken,
        error: null,
      }));
      lastActivityRef.current = Date.now();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed. Please check your credentials.';
      setState((prev) => ({
        ...prev,
        isAuthenticated: false,
        isLoading: false,
        token: null,
        error: message,
      }));
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // For an OAuth-configured client, Amplify's signOut() performs the Hosted
      // UI logout redirect automatically so the Hosted UI + federated session
      // are also ended and re-authentication is required (Req 9.3, 9.6). For
      // email/password sessions this simply clears the Cognito session.
      await signOut();
    } catch {
      // Ignore sign-out errors
    }
    setState({
      isAuthenticated: false,
      isLoading: false,
      token: null,
      error: null,
      resendCooldownUntil: null,
    });
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      // No forceRefresh: Amplify returns the cached token and only refreshes it
      // (via the refresh token) when it is near or past expiry, avoiding a
      // needless refresh round-trip on every API call.
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString() ?? null;
      if (idToken) {
        setState((prev) => ({ ...prev, token: idToken }));
      }
      return idToken;
    } catch {
      // Token refresh failure clears the local session and requires re-auth
      // (Req 9.7) — reuse the existing expiry path.
      handleSessionExpiry();
      return null;
    }
  }, [handleSessionExpiry]);

  const register = useCallback(
    async (email: string, password: string): Promise<RegisterResult> => {
      // Client-side pre-validation BEFORE any account-creation request so no
      // signUp call is made for an invalid email/password (Req 1.5, 1.6).
      if (!isValidEmail(email)) {
        setState((prev) => ({ ...prev, error: INVALID_EMAIL_MESSAGE }));
        throw new Error(INVALID_EMAIL_MESSAGE);
      }
      if (!validatePasswordPolicy(password).isValid) {
        setState((prev) => ({ ...prev, error: PASSWORD_POLICY_MESSAGE }));
        throw new Error(PASSWORD_POLICY_MESSAGE);
      }

      const result: RegisterResult = {
        nextStep: 'CONFIRM_SIGN_UP',
        destinationMasked: maskEmail(email),
      };

      try {
        await signUp({
          username: email,
          password,
          options: { userAttributes: { email } },
        });
        setState((prev) => ({ ...prev, error: null }));
        return result;
      } catch (err) {
        // Non-enumerating: an already-registered email resolves to the SAME
        // "verification required" outcome as a fresh sign-up, without
        // confirming that an account exists (Req 1.7).
        if (errorName(err) === 'UsernameExistsException') {
          setState((prev) => ({ ...prev, error: null }));
          return result;
        }
        // Network/service error — surface a retryable message (Req 1.9). The UI
        // retains entered values and returns the User to the form.
        setState((prev) => ({ ...prev, error: REGISTRATION_FAILED_MESSAGE }));
        throw new Error(REGISTRATION_FAILED_MESSAGE);
      }
    },
    [],
  );

  const confirmRegistration = useCallback(
    async (email: string, code: string): Promise<void> => {
      try {
        // Confirm against the EXACT email captured at sign-up (Req 2.3).
        await confirmSignUp({ username: email, confirmationCode: code });
        setState((prev) => ({ ...prev, error: null }));
      } catch (err) {
        // Map the confirmation errors to friendly messages per the Error
        // Handling table; the pending verification state is preserved so the
        // User can submit another code (Req 2.6, 2.7).
        const name = errorName(err);
        if (name === 'CodeMismatchException') {
          throw new Error(CODE_INCORRECT_MESSAGE);
        }
        if (name === 'ExpiredCodeException') {
          throw new Error(CODE_EXPIRED_MESSAGE);
        }
        throw err;
      }
    },
    [],
  );

  const resendCode = useCallback(async (email: string): Promise<void> => {
    // Enforce the 30-second client cooldown: no-op/reject while active (Req 3.5).
    const now = Date.now();
    const cooldownUntil = resendCooldownUntilRef.current;
    if (cooldownUntil !== null && now < cooldownUntil) {
      return;
    }

    try {
      await resendSignUpCode({ username: email });
      // Start the cooldown window so the UI can disable the resend control.
      const until = Date.now() + RESEND_COOLDOWN_MS;
      resendCooldownUntilRef.current = until;
      setState((prev) => ({ ...prev, error: null, resendCooldownUntil: until }));
      setTimeout(() => {
        // Re-enable the control when the cooldown elapses (Req 3.5).
        if (resendCooldownUntilRef.current === until) {
          resendCooldownUntilRef.current = null;
          setState((prev) =>
            prev.resendCooldownUntil === until
              ? { ...prev, resendCooldownUntil: null }
              : prev,
          );
        }
      }, RESEND_COOLDOWN_MS);
    } catch (err) {
      // Resend rate-limit maps to a wait message; pending state is retained
      // (Req 3.4).
      const name = errorName(err);
      if (
        name === 'LimitExceededException' ||
        name === 'TooManyRequestsException'
      ) {
        setState((prev) => ({ ...prev, error: RESEND_RATE_LIMIT_MESSAGE }));
        throw new Error(RESEND_RATE_LIMIT_MESSAGE);
      }
      throw err;
    }
  }, []);

  const signInWithProvider = useCallback(
    async (provider: FederatedProvider): Promise<void> => {
      // Amplify generates the PKCE verifier and >=16-byte OAuth state and
      // validates them on return (Req 10.1). This triggers a full-page redirect
      // to the Hosted UI (Req 5.3); the post-redirect session is picked up by
      // the Hub subscription above.
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      await signInWithRedirect({ provider });
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        getToken,
        register,
        confirmRegistration,
        resendCode,
        signInWithProvider,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
